// TYPES
import { useState } from 'react';
import { useSelector } from 'react-redux';
import type { IPermission } from '@timeedit/types/lib/types';
import { Icon } from '@timeedit/ui-components';
import {
  Button,
  Popconfirm,
  Select,
  Space,
  Table,
} from '@timeedit/ui-components/antd';
// i18N
import intl from '../../../i18n/intl';
// REDUX
import { appPermissionsSelector } from '../../../slices/permission.slice';

const language: any = intl.messages;

type Props = {
  userAppPermissionValues: string[];
  onChange: (scopes: string[]) => void;
  disabled?: boolean;
};

const AppPermissionsTable = ({
  userAppPermissionValues,
  onChange,
  disabled = false,
}: Props) => {
  const appPermissions: IPermission[] = useSelector(appPermissionsSelector);
  const [newValue, setNewValue] = useState<string | undefined>(undefined);

  // EVENT HANDLERS
  const onAdd = () => {
    if (!newValue) return;
    onChange([...userAppPermissionValues, newValue]);
    setNewValue(undefined);
  };

  const onDelete = (value: string) => {
    const idx = userAppPermissionValues.indexOf(value);
    if (idx === -1) return;
    onChange([
      ...userAppPermissionValues.slice(0, idx),
      ...userAppPermissionValues.slice(idx + 1),
    ]);
  };

  // Columns
  return (
    <div>
      <Table
        size="small"
        dataSource={appPermissions.filter(
          (aP) => userAppPermissionValues.indexOf(aP.value) > -1
        )}
        columns={[
          {
            title: 'Name',
            dataIndex: 'name',
            key: 'name',
            width: 260,
          },
          {
            title: 'Description',
            dataIndex: 'description',
            key: 'description',
            ellipsis: true,
            width: 260,
          },
          {
            title: 'Value',
            dataIndex: 'value',
            key: 'value',
          },
          {
            title: language.action,
            key: 'action',
            dataIndex: undefined,
            render: (_, appPermission) => (
              <Space size="middle">
                <Popconfirm
                  disabled={disabled}
                  title={language.sure_to_delete}
                  onConfirm={() => onDelete(appPermission.value)}
                  okText={language.yes}
                  cancelText={language.no}
                >
                  <Button
                    size="small"
                    danger
                    ghost
                    disabled={disabled}
                    type="default"
                    className="types-list-action-button-delete"
                    icon={<Icon id="DeleteOutlined" alt="Delete" />}
                  />
                </Popconfirm>
              </Space>
            ),
          },
        ]}
        rowKey="value"
        style={{ marginBottom: '16px', marginTop: '16px' }}
        pagination={false}
        footer={() => (
          <Space direction="horizontal">
            <Select
              disabled={disabled}
              placeholder="Select an app permission to add..."
              value={newValue}
              onChange={(val) => setNewValue(val)}
              size="small"
              style={{ width: '200px' }}
            >
              {appPermissions
                .filter(
                  (appPermission) =>
                    !userAppPermissionValues.includes(appPermission.value)
                )
                .map((appPermission) => (
                  <Select.Option
                    value={appPermission.value}
                    key={appPermission.value}
                  >
                    {appPermission.name}
                  </Select.Option>
                ))}
            </Select>
            <Button
              type="default"
              size="small"
              icon={<Icon id="PlusOutlined" alt="Plus" />}
              onClick={onAdd}
              disabled={!newValue || disabled}
            >
              Add app permission
            </Button>
          </Space>
        )}
      />
    </div>
  );
};

export default AppPermissionsTable;
